/**
 * Composio frontend helpers.
 *
 * Small constants, lookups, and label/colour maps shared between the
 * integrations API client, the Zustand store, the Settings UI, and the
 * canvas connection frames. Anything that hits the network lives in
 * ``@/api/integrations`` instead.
 */

import type {
  ConnectionStatus,
  Toolkit,
  ToolkitPage,
} from "@/api/integrations";
import { translate } from "@/i18n";
import { libNoticesMessages } from "@/i18n/messages/lib-notices";

/**
 * Master switch for the Composio UI surface (Settings → Integrations tab,
 * connection-frame, connection-prompt-frame). Phase 1 + 2 ship the
 * backend and MCP server, so the default is ``true``. Operators can flip
 * ``VITE_ENABLE_COMPOSIO=false`` to hide the UI without a code change
 * (e.g. while rotating API keys).
 */
export const COMPOSIO_UI_ENABLED: boolean =
  (import.meta.env.VITE_ENABLE_COMPOSIO as string | undefined) !== "false";

/**
 * Normalise a toolkit slug the way the backend does
 * (``_normalise_toolkit_slug`` in services.py): trim, lowercase. We
 * deliberately keep this dumb on the client — the backend still owns
 * the authoritative validation.
 */
export function normalizeToolkitSlug(input: unknown): string {
  return typeof input === "string" ? input.trim().toLowerCase() : "";
}

/**
 * The apps a Connect card lists, normalised and without repeats, read like
 * the server (``prompt_toolkit_slugs``): the one ``toolkit_slug`` (older
 * ``app_name``) of a card made before cards listed several apps, then its
 * ``toolkit_slugs`` list.
 */
export function connectionPromptSlugs(
  content: Record<string, unknown> | undefined,
): string[] {
  const listed = content?.toolkit_slugs;
  const raw = [
    content?.toolkit_slug || content?.app_name,
    ...(Array.isArray(listed) ? listed : [listed]),
  ];
  return [...new Set(raw.map(normalizeToolkitSlug))].filter(Boolean);
}

/**
 * Find a toolkit by slug in a catalogue list. Returns ``undefined`` if
 * the catalogue is empty or the slug is not present. Used by the
 * connection-prompt frame to enrich the agent-supplied slug with a
 * display name and icon.
 */
export function findToolkit(
  slug: string,
  catalog: readonly Toolkit[],
): Toolkit | undefined {
  const target = normalizeToolkitSlug(slug);
  if (!target) return undefined;
  return catalog.find((t) => normalizeToolkitSlug(t.name) === target);
}

/**
 * The name people see for an app: the catalogue's display name, else the
 * slug in words ("google_search_console" becomes "Google Search Console"),
 * so a raw slug never reaches the screen.
 */
export function toolkitDisplayName(
  slug: string,
  toolkit: Toolkit | undefined,
): string {
  if (toolkit?.display_name) return toolkit.display_name;
  return slug
    .split(/[_-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * One catalogue page in its current shape. Older backends returned a bare
 * array, and a malformed page must never replace loaded toolkits.
 */
export function normaliseToolkitPage(
  page: unknown,
  defaults: { page: number; total?: number } = { page: 1 },
): Omit<ToolkitPage, "page_size"> {
  if (Array.isArray(page)) {
    return {
      items: page,
      total: defaults.total ?? page.length,
      page: defaults.page,
      has_more: false,
    };
  }
  const candidate = page as Partial<ToolkitPage> | null;
  const items = Array.isArray(candidate?.items) ? candidate.items : [];
  return {
    items,
    total:
      typeof candidate?.total === "number"
        ? candidate.total
        : (defaults.total ?? items.length),
    page: typeof candidate?.page === "number" ? candidate.page : defaults.page,
    has_more:
      typeof candidate?.has_more === "boolean" ? candidate.has_more : false,
  };
}

const STATUS_LABEL_KEY = {
  active: "statusActive",
  initiated: "statusInitiated",
  expired: "statusExpired",
  failed: "statusFailed",
  inactive: "statusInactive",
} as const satisfies Record<ConnectionStatus, string>;

/**
 * The user-facing label for the lowercase status enum the backend exposes,
 * in the app language. Short — it appears in status badges next to the
 * toolkit name.
 */
export function connectionStatusLabel(status: ConnectionStatus): string {
  return translate(libNoticesMessages, STATUS_LABEL_KEY[status]);
}

/**
 * Badge variants per status. Maps onto the variants exported from
 * ``@/components/ui/badge``. Picking ``status-success`` / ``status-error``
 * keeps the UI on the design-system tokens — no raw colour literals.
 */
export const STATUS_BADGE_VARIANT: Record<
  ConnectionStatus,
  "status-success" | "status-error" | "warning" | "outline"
> = {
  active: "status-success",
  initiated: "warning",
  expired: "warning",
  failed: "status-error",
  inactive: "outline",
};
